import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'
import type { ParcelIssue } from './useParcelIssues'

export interface IssueMessage {
  id: string
  issue_id: string
  author_id: string | null
  author_role: 'customer' | 'staff'
  author_name: string | null
  body: string
  is_internal: boolean
  created_at: string
}

export interface IssueAttachment {
  id: string
  issue_id: string
  file_name: string
  file_size: number | null
  mime_type: string | null
  storage_path: string | null
  public_url: string | null
  uploaded_by: string | null
  description: string | null
  created_at: string
}

export interface IssueStatusLog {
  id: string
  issue_id: string
  from_status: string | null
  to_status: string
  changed_by: string | null
  reason: string | null
  created_at: string
}

export function useParcelIssue(id: string | undefined) {
  const [issue, setIssue] = useState<ParcelIssue | null>(null)
  const [messages, setMessages] = useState<IssueMessage[]>([])
  const [attachments, setAttachments] = useState<IssueAttachment[]>([])
  const [statusLog, setStatusLog] = useState<IssueStatusLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const [issueRes, msgRes, attRes, logRes] = await Promise.all([
        supabase
          .from('parcel_issues')
          .select('*, customers(name)')
          .eq('id', id)
          .single(),
        supabase
          .from('parcel_issue_messages')
          .select('*')
          .eq('issue_id', id)
          .order('created_at', { ascending: true }),
        supabase
          .from('parcel_issue_attachments')
          .select('*')
          .eq('issue_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('parcel_issue_status_log')
          .select('*')
          .eq('issue_id', id)
          .order('created_at', { ascending: true }),
      ])

      if (issueRes.error) throw issueRes.error
      const raw = issueRes.data as any
      setIssue({ ...raw, agent_name: raw.customers?.name ?? null, customers: undefined })
      setMessages((msgRes.data || []) as IssueMessage[])
      setAttachments((attRes.data || []) as IssueAttachment[])
      setStatusLog((logRes.data || []) as IssueStatusLog[])
    } catch (e: any) {
      setError(e.message || 'Failed to load issue')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  const updateIssue = useCallback(async (values: Partial<ParcelIssue>) => {
    if (!id) throw new Error('No issue id')
    const { data, error } = await supabase
      .from('parcel_issues')
      .update({ ...values, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    await load()
    return data as ParcelIssue
  }, [id, load])

  const changeStatus = useCallback(async (
    toStatus: string,
    changedBy: string | null,
    reason: string | null,
  ) => {
    if (!id || !issue) throw new Error('No issue')
    const patch: Record<string, any> = {
      status: toStatus,
      updated_at: new Date().toISOString(),
    }
    if (toStatus === 'investigating' && !issue.investigation_started_at) {
      patch.investigation_started_at = new Date().toISOString()
    }
    if ((toStatus === 'resolved' || toStatus === 'closed') && !issue.resolved_at) {
      patch.resolved_at = new Date().toISOString()
    }

    const [updateRes, logRes] = await Promise.all([
      supabase.from('parcel_issues').update(patch).eq('id', id),
      supabase.from('parcel_issue_status_log').insert([{
        issue_id: id,
        from_status: issue.status,
        to_status: toStatus,
        changed_by: changedBy,
        reason,
      }]),
    ])
    if (updateRes.error) throw updateRes.error
    if (logRes.error) throw logRes.error
    await load()
  }, [id, issue, load])

  const addMessage = useCallback(async (
    body: string,
    authorId: string | null,
    authorName: string | null,
    authorRole: 'staff' | 'customer',
    isInternal: boolean,
  ) => {
    if (!id) throw new Error('No issue id')
    const { error } = await supabase.from('parcel_issue_messages').insert([{
      issue_id: id,
      body,
      author_id: authorId,
      author_name: authorName,
      author_role: authorRole,
      is_internal: isInternal,
    }])
    if (error) throw error
    await load()
  }, [id, load])

  const addAttachment = useCallback(async (values: Partial<IssueAttachment>) => {
    if (!id) throw new Error('No issue id')
    const { error } = await supabase.from('parcel_issue_attachments').insert([{
      ...values,
      issue_id: id,
    }])
    if (error) throw error
    await load()
  }, [id, load])

  const uploadFile = useCallback(async (file: File, uploadedBy: string | null, description?: string) => {
    if (!id) throw new Error('No issue id')
    const ext = file.name.split('.').pop()
    const path = `${id}/${Date.now()}.${ext}`
    const { error: uploadErr } = await supabase.storage
      .from('parcel-issues')
      .upload(path, file)
    if (uploadErr) throw uploadErr

    const { data: urlData } = supabase.storage.from('parcel-issues').getPublicUrl(path)
    await addAttachment({
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type,
      storage_path: path,
      public_url: urlData?.publicUrl ?? null,
      uploaded_by: uploadedBy,
      description: description ?? null,
    })
  }, [id, addAttachment])

  return {
    issue,
    messages,
    attachments,
    statusLog,
    loading,
    error,
    reload: load,
    updateIssue,
    changeStatus,
    addMessage,
    addAttachment,
    uploadFile,
  }
}
